import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getAppServices } from "../../client/services/services";
import {
    AUTHENTICATION_STORED_ITEM,
    authenticationItemFromStatus,
    buildAuthenticationItem,
} from "../../shared/utils/authentication-item.utils";
import { InternalActionName, useInternalAction } from "../../state/InternalContext";
import { useSiteContext } from "../../state/SiteContext";
import ChangePassword from "../user-functions/ChangePassword";
import ForgotPassword from "../user-functions/ForgotPassword";
import Login from "./Login";

export interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: any | null;
    error: string | null;
    authErrorStatus: number | null; // 401 or 403 from server cookie
}

interface AuthContextType {
    // Modal state
    showLogin: (force?: boolean) => void;
    closeLogin: () => void;
    isLoginOpen: boolean;
    showForgotPassword: (email?: string) => void;
    closeForgotPassword: () => void;
    isForgotPasswordOpen: boolean;
    showChangePassword: () => void;
    closeChangePassword: () => void;
    isChangePasswordOpen: boolean;

    // Auth state
    isAuthenticated: boolean;
    isLoading: boolean;
    loading: string | null;
    user: any | null;
    error: string | null;
    authErrorStatus: number | null; // 401 or 403 from server cookie

    // Auth actions
    login: (credentials: { email: string; password: string }) => Promise<{ success: boolean; message?: string }>;
    loginWithProvider: (provider: string) => void;
    logout: () => Promise<void>;
    clearError: () => void;
}

interface AuthProviderProps {
    children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
    const { siteState } = useSiteContext();
    const siteName = siteState.site?.name
        || (typeof window === "undefined" ? "" : window.location.pathname.split("/")[1] || "");
    const services = useMemo(() => getAppServices(), []);
    const setStoredItem = useInternalAction(InternalActionName.SET_STORED_ITEM);

    const writeSignedOut = useCallback(() => {
        setStoredItem({
            [AUTHENTICATION_STORED_ITEM]: buildAuthenticationItem({ authenticated: false }),
        });
    }, [setStoredItem]);

    // Modal state
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);
    const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>("");
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
    const [forceLogin, setForceLogin] = useState(false);
    const [loading, setLoading] = useState<string | null>(null);

    // Signed out and loading on the first server and client render.
    // The httpOnly jwt cookie is not visible to document.cookie, so status is the session check.
    const [authState, setAuthState] = useState<AuthState>({
        isAuthenticated: false,
        isLoading: true,
        user: null,
        error: null,
        authErrorStatus: null,
    });

    /**
     * Helper function to get a cookie value by name
     * 
     * @param name - The cookie name
     * @returns The cookie value or null if not found
     */
    const getCookie = useCallback((name: string): string | null => {
        if (typeof document === 'undefined') return null;
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) {
            return parts.pop()?.split(';').shift() || null;
        }
        return null;
    }, []);

    /**
     * Helper function to clear a cookie
     * 
     * @param name - The cookie name to clear
     */
    const clearCookie = useCallback((name: string): void => {
        if (typeof document === 'undefined') return;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }, []);

    /**
     * Check for server-set auth error cookie and handle it
     * This cookie is set by the server when a page is served but has an auth error (401/403)
     */
    const checkServerAuthError = useCallback(() => {
        if (typeof window === 'undefined') return;

        const authError = getCookie('auth_error');
        if (authError) {
            const errorStatus = parseInt(authError, 10);
            console.log(`Server auth error detected: ${errorStatus}`);
            // Clear the cookie after reading it
            clearCookie('auth_error');
            // Store error status in state for components to use
            setAuthState(prev => ({
                ...prev,
                authErrorStatus: errorStatus,
            }));
            // Handle based on error status
            if (errorStatus === 401) {
                // Unauthorized - show login modal
                setForceLogin(true);
                setIsLoginOpen(true);
            } else if (errorStatus === 403) {
                // Forbidden - user is authenticated but doesn't have permission
                setAuthState(prev => ({
                    ...prev,
                    error: "You don't have permission to access this content",
                }));
            }
        }
    }, [getCookie, clearCookie]);

    // Check authentication status on mount and when returning from provider login
    // Writes `_authentication` only after a status result. Setting isLoading does not dispatch,
    // so a focus recheck leaves the server seed in place until this check finishes.
    const checkAuth = useCallback(async () => {
        if (!siteName) {
            setAuthState(prev => ({ ...prev, isAuthenticated: false, isLoading: false, user: null }));
            writeSignedOut();
            return;
        }
        setAuthState(prev => ({ ...prev, isLoading: true }));
        try {
            const authResult = await services.auth.checkAuthStatus(siteName);
            setStoredItem({
                [AUTHENTICATION_STORED_ITEM]: authenticationItemFromStatus(authResult),
            });
            setAuthState(prev => ({
                ...prev,
                isAuthenticated: authResult.isAuthenticated && authResult.user?.id !== "guest", //guests have no user object (isAuthenticated will be false)
                user: authResult.user || null,
                isLoading: false,
                error: null,
            }));

            // Clear provider loading state when auth check completes (user returned from provider)
            setLoading(null);

            // If user is authenticated, close login modals and reset force login
            if (authResult.isAuthenticated && authResult.user?.id !== "guest") {
                setIsLoginOpen(false);
                setIsForgotPasswordOpen(false);
                setForceLogin(false);
                // Clear auth error status on successful authentication
                setAuthState(prev => ({
                    ...prev,
                    authErrorStatus: null,
                }));
            }

            // If user is authenticated and we have a stored redirect path, redirect back to it
            if (authResult.isAuthenticated && typeof window !== 'undefined') {
                const redirectPath = sessionStorage.getItem('loginRedirectPath');
                const currentPath = window.location.pathname + window.location.search;

                if (redirectPath && redirectPath !== currentPath) {
                    // Redirect back to original location
                    sessionStorage.removeItem('loginRedirectPath');
                    window.location.href = redirectPath;
                    return;
                }
            }
        } catch (error) {
            setAuthState(prev => ({
                ...prev,
                isLoading: false,
                error: "Failed to check authentication status",
            }));
            // Clear provider loading state on error
            setLoading(null);
        }
    }, [siteName, services.auth, setStoredItem, writeSignedOut]);

    useEffect(() => {
        // Check for server-set auth error cookie first (set during SSR)
        checkServerAuthError();
        checkAuth();

        // Check auth status when window regains focus (e.g., returning from provider login)
        const handleFocus = () => {
            checkAuth();
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [checkAuth, checkServerAuthError]);

    // Modal actions
    const showLogin = (force?: boolean) => {
        setForceLogin(force || false);
        setIsLoginOpen(true);
    };

    const closeLogin = () => {
        if (forceLogin) {
            return;
        }
        setIsLoginOpen(false);
    };

    const showForgotPassword = useCallback((email?: string) => {
        setForgotPasswordEmail(email || "");
        setIsForgotPasswordOpen(true);
        setIsLoginOpen(false); // Close login modal when opening forgot password
    }, []);

    const closeForgotPassword = useCallback(() => {
        setIsForgotPasswordOpen(false);
        setForgotPasswordEmail("");
    }, []);

    const showChangePassword = useCallback(() => {
        setIsChangePasswordOpen(true);
    }, []);

    const closeChangePassword = useCallback(() => {
        setIsChangePasswordOpen(false);
    }, []);

    // Auth actions
    const login = useCallback(async (credentials: { email: string; password: string }) => {
        if (!siteName) return { success: false, message: "No site name available" };

        const hadAuthError = authState.authErrorStatus === 401;
        setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            await services.auth.localLogin(siteName, credentials.email, credentials.password);
            await checkAuth();

            // Close modal
            setIsLoginOpen(false);

            // If there was a 401 error, reload the page to fetch authorized content
            if (hadAuthError && typeof window !== 'undefined') {
                window.location.reload();
                return { success: true };
            }

            return { success: true };
        } catch (error: any) {
            const errorMessage = error.message || "Login failed";
            setAuthState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
            }));
            return { success: false, message: errorMessage };
        }
    }, [siteName, services.auth, authState.authErrorStatus, checkAuth]);

    const loginWithProvider = useCallback((provider: string) => {
        if (!siteName) return;

        // Set loading state for the provider
        setLoading(provider);

        // Store current location before redirecting to provider
        const currentPath = window.location.pathname + window.location.search;
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('loginRedirectPath', currentPath);
        }

        services.auth.providerLogin(siteName, provider);
    }, [siteName, services.auth]);

    const logout = useCallback(async () => {
        if (!siteName) return;

        setAuthState(prev => ({ ...prev, isLoading: true }));

        try {
            await services.auth.logout(siteName);

            // Clear redirect path on logout
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem('loginRedirectPath');
                // Force a hard redirect/refresh to ensure all state is cleared
                // Using replace() prevents back button from returning to logged-out state
                window.location.replace(`/${siteName}`);
            }
        } catch (error: any) {
            console.error("Logout API call failed:", error);
            setAuthState(prev => ({
                ...prev,
                isLoading: false,
                error: "Logout failed",
            }));
        }
    }, [siteName, services.auth]);

    const clearError = useCallback(() => {
        setAuthState(prev => ({ ...prev, error: null }));
    }, []);

    const contextValue: AuthContextType = {
        // Modal state
        showLogin,
        closeLogin,
        isLoginOpen,
        showForgotPassword,
        closeForgotPassword,
        isForgotPasswordOpen,
        showChangePassword,
        closeChangePassword,
        isChangePasswordOpen,

        // Auth state
        ...authState,
        loading,

        // Auth actions
        login,
        loginWithProvider,
        logout,
        clearError,
    };

    return (
        <AuthContext.Provider value={contextValue}>
            {children}
            <Login open={isLoginOpen} onClose={closeLogin} />
            <ForgotPassword
                open={isForgotPasswordOpen}
                onClose={closeForgotPassword}
                initialEmail={forgotPasswordEmail}
            />
            <ChangePassword
                open={isChangePasswordOpen}
                onClose={closeChangePassword}
            />
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

// Export useLogin as alias for backwards compatibility
export const useLogin = useAuth;

